import { useMemo, Children, Fragment, cloneElement } from 'react';
import styled from 'styled-components';
import { Divider } from '@cbhq/cds-web/layout';
import { media } from '../../breakpoints.js';
import { componentsThemeMap } from '../../componentsThemeMap.js';
import { ANALYTICS_KEY } from '../../constants.js';
import { SectionComponentLayout } from '../../layout/SectionComponentLayout.js';
import { AnalyticsProvider } from '../../providers/AnalyticsProvider.js';
import '../../ui/Div/index.js';
import { ErrorBoundary } from '../../ui/ErrorBoundary.js';
import { LinkButton } from '../../ui/Link/LinkButton.js';
import { Text } from '../../ui/Text.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { Div } from '../../ui/Div/Div.js';
function Wayfinding({
  headline,
  body,
  style = 'default',
  columns,
  rows = 'all',
  hasSpacingTop = false,
  hasSpacingHorizontal = true,
  ctaButtonLabel,
  ctaButtonUrl,
  children,
  compact,
  hasOneColumnDivider,
  ctaPosition = 'bottom',
  aspectRatio = 'long',
  anchor,
  as
}) {
  const _componentsThemeMap$s = componentsThemeMap[style],
    foreground = _componentsThemeMap$s.foreground,
    foregroundSecondary = _componentsThemeMap$s.foregroundSecondary,
    background = _componentsThemeMap$s.background;
  const childrenToRender = useMemo(() => {
    let arrayChildren = Children.toArray(children);
    if (rows !== 'all') arrayChildren = arrayChildren.slice(0, rows * columns);
    return arrayChildren.map((child, index) => {
      return /*#__PURE__*/jsx(Fragment, {
        children: /*#__PURE__*/jsxs(AnalyticsProvider, {
          analyticsPrefix: `${ANALYTICS_KEY.Child}${index}`,
          children: [/*#__PURE__*/cloneElement(child, {
            style,
            largeThumbnail: columns === 2,
            long: columns === 1,
            compact,
            aspectRatio
          }), hasOneColumnDivider && columns === 1 && index !== arrayChildren.length - 1 && /*#__PURE__*/jsx(Divider, {
            opacity: 1
          })]
        })
      }, index);
    });
  }, [children, rows, columns, style, compact, hasOneColumnDivider, aspectRatio]);
  return /*#__PURE__*/jsx(ErrorBoundary, {
    children: /*#__PURE__*/jsx(AnalyticsProvider, {
      analyticsPrefix: "Wayfinding",
      children: /*#__PURE__*/jsx(SectionComponentLayout, {
        hasSpacingTop: hasSpacingTop,
        hasSpacingHorizontal: hasSpacingHorizontal,
        background: background,
        anchor: anchor,
        hasNonDefaultBackgroundSpacingVertical: true,
        as: as,
        children: /*#__PURE__*/jsxs(Div, {
          flexDirection: "column",
          gap: 7,
          children: [(headline || body) && /*#__PURE__*/jsxs(Div, {
            flexDirection: {
              sm: 'column',
              md: 'row'
            },
            justifyContent: "space-between",
            alignItems: "flex-end",
            gap: 1,
            children: [/*#__PURE__*/jsxs(Div, {
              flexDirection: "column",
              width: "100%",
              gap: 1,
              children: [headline && /*#__PURE__*/jsx(Text, {
                as: "h2",
                variant: {
                  sm: 'title2',
                  lg: compact ? 'display2' : 'display3'
                },
                color: foreground,
                children: headline
              }), body && /*#__PURE__*/jsx(Text, {
                as: "p",
                variant: {
                  sm: 'body',
                  lg: 'title4'
                },
                color: foregroundSecondary,
                children: body
              })]
            }), ctaPosition === 'top' && ctaButtonLabel && ctaButtonUrl && /*#__PURE__*/jsx(Div, {
              flexDirection: "column",
              display: {
                sm: 'none',
                md: 'flex'
              },
              maxWidth: {
                sm: '100%',
                md: '75%'
              },
              gap: 1,
              children: /*#__PURE__*/jsx(LinkButton, {
                href: ctaButtonUrl,
                variant: "secondary",
                "data-qa": ANALYTICS_KEY.ButtonLabel,
                children: ctaButtonLabel
              })
            })]
          }), childrenToRender && /*#__PURE__*/jsx(Div, {
            "data-qa": ANALYTICS_KEY.CardGrid,
            display: "grid",
            gridTemplateColumns: {
              sm: '1fr',
              md: columns === 1 ? '1fr' : '1fr 1fr',
              lg: `repeat(${columns}, 1fr)`
            },
            columnGap: {
              sm: 3,
              md: 4
            },
            rowGap: {
              sm: 7,
              md: 8,
              lg: !hasOneColumnDivider && columns === 1 ? 11 : 8
            },
            maxWidth: compact ? {
              sm: '100%',
              lg: 900
            } : '100%',
            children: /*#__PURE__*/jsx(SkipRows, {
              total: childrenToRender.length,
              columns: columns,
              rows: rows,
              children: childrenToRender
            })
          }), ctaPosition === 'bottom' && ctaButtonLabel && ctaButtonUrl && /*#__PURE__*/jsx(Div, {
            justifyContent: "center",
            display: {
              sm: 'flex',
              md: compact ? 'none' : 'flex'
            },
            children: /*#__PURE__*/jsx(LinkButton, {
              href: ctaButtonUrl,
              "data-qa": ANALYTICS_KEY.ButtonLabel,
              children: ctaButtonLabel
            })
          })]
        })
      })
    })
  });
}
const SkipRows = styled.div.withConfig({
  shouldForwardProp: p => !['total', 'columns', 'rows'].includes(p)
})`
  display: contents;

  ${({
  rows,
  columns,
  total
}) => columns > 2 && rows !== 'all' && total % 2 !== 0 && `
    > div:last-child {
      display: flex;
    }

    ${media.md} {
      > div:last-child {
        display: none;
      }
    }

    ${media.lg} {
      > div:last-child {
        display: flex;
      }
  `}
`;
export { Wayfinding };
//# sourceMappingURL=Wayfinding.js.map
